// Sistema de logging para producción
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

export interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: Date;
  context?: string;
  data?: Record<string, unknown>;
  userId?: string;
  sessionId?: string;
  error?: Error;
}

class Logger {
  private minLevel: LogLevel;
  private isProduction: boolean;

  constructor() {
    this.isProduction = import.meta.env.PROD;
    this.minLevel = this.isProduction ? LogLevel.WARN : LogLevel.DEBUG;
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.minLevel;
  }

  private formatMessage(entry: LogEntry): string {
    const timestamp = entry.timestamp.toISOString();
    const level = LogLevel[entry.level];
    const context = entry.context ? `[${entry.context}]` : "";

    return `${timestamp} ${level} ${context} ${entry.message}`;
  }

  private logToConsole(entry: LogEntry): void {
    if (this.isProduction) return; // No logs en consola en producción

    const formattedMessage = this.formatMessage(entry);

    switch (entry.level) {
      case LogLevel.DEBUG:
        console.debug(formattedMessage, entry.data);
        break;
      case LogLevel.INFO:
        console.info(formattedMessage, entry.data);
        break;
      case LogLevel.WARN:
        console.warn(formattedMessage, entry.data);
        break;
      case LogLevel.ERROR:
        console.error(formattedMessage, entry.data, entry.error);
        break;
    }
  }

  private async logToRemote(entry: LogEntry): Promise<void> {
    if (!this.isProduction) return; // Solo en producción

    try {
      // En producción, enviar logs a servicio de logging
      // Usar la ruta correcta del backend
      const response = await fetch("/api/logs", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          level: LogLevel[entry.level],
          message: entry.message,
          timestamp: entry.timestamp.toISOString(),
          context: entry.context,
          data: entry.data,
          userId: entry.userId,
          sessionId: entry.sessionId,
          error: entry.error
            ? {
                name: entry.error.name,
                message: entry.error.message,
                stack: entry.error.stack,
              }
            : undefined,
        }),
      });

      // Si la respuesta no es ok, no hacer nada (evitar loops)
      if (!response.ok && response.status === 404) {
        console.warn("[LOGGER] Servicio de logs no disponible en el backend");
      }
    } catch (error) {
      // Si falla el logging remoto, no hacer nada para evitar loops
      // Solo loggear en desarrollo
      if (!this.isProduction) {
        console.warn("[LOGGER] Failed to send log to remote service:", error);
      }
    }
  }

  private log(
    level: LogLevel,
    message: string,
    context?: string,
    data?: Record<string, unknown>,
    error?: Error
  ): void {
    if (!this.shouldLog(level)) return;

    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date(),
      context,
      data,
      error,
      userId: this.getCurrentUserId(),
      sessionId: this.getSessionId(),
    };

    this.logToConsole(entry);
    this.logToRemote(entry);
  }

  private getCurrentUserId(): string | undefined {
    try {
      const user = localStorage.getItem("user");
      return user ? JSON.parse(user).uid : undefined;
    } catch {
      return undefined;
    }
  }

  private getSessionId(): string | undefined {
    try {
      return sessionStorage.getItem("sessionId") || undefined;
    } catch {
      return undefined;
    }
  }

  debug(
    message: string,
    context?: string,
    data?: Record<string, unknown>
  ): void {
    this.log(LogLevel.DEBUG, message, context, data);
  }

  info(
    message: string,
    context?: string,
    data?: Record<string, unknown>
  ): void {
    this.log(LogLevel.INFO, message, context, data);
  }

  warn(
    message: string,
    context?: string,
    data?: Record<string, unknown>
  ): void {
    this.log(LogLevel.WARN, message, context, data);
  }

  error(
    message: string,
    error?: Error,
    context?: string,
    data?: Record<string, unknown>
  ): void {
    this.log(LogLevel.ERROR, message, context, data, error);
  }

  // Métodos de conveniencia para servicios específicos
  service(serviceName: string) {
    return {
      debug: (message: string, data?: Record<string, unknown>) =>
        this.debug(message, serviceName, data),
      info: (message: string, data?: Record<string, unknown>) =>
        this.info(message, serviceName, data),
      warn: (message: string, data?: Record<string, unknown>) =>
        this.warn(message, serviceName, data),
      error: (message: string, error?: Error, data?: Record<string, unknown>) =>
        this.error(message, error, serviceName, data),
    };
  }

  // Método para logging de performance
  performance(action: string, duration: number, context?: string): void {
    this.info(`Performance: ${action} completed in ${duration}ms`, context, {
      action,
      duration,
    });
  }

  // Método para logging de eventos de usuario
  userAction(action: string, data?: Record<string, unknown>): void {
    this.info(`User action: ${action}`, "USER_ACTION", data);
  }
}

// Exportar instancia singleton
export const logger = new Logger();

export const logInfo = (message: string, data?: unknown) => {
  logger.info(
    message,
    undefined,
    typeof data === "object" ? (data as Record<string, unknown>) : { data }
  );
};

export const logError = (message: string, error?: unknown) => {
  logger.error(
    message,
    error instanceof Error ? error : new Error(String(error))
  );
};

export const logWarning = (message: string, data?: unknown) => {
  logger.warn(
    message,
    undefined,
    typeof data === "object" ? (data as Record<string, unknown>) : { data }
  );
};

export const logDebug = (message: string, data?: unknown) => {
  logger.debug(
    message,
    undefined,
    typeof data === "object" ? (data as Record<string, unknown>) : { data }
  );
};
